import { useMemo, useState } from 'react';
import { AlertCircle, Check, Loader2, MapPin, Search, UserPlus, Users, X } from 'lucide-react';

export interface RivhitCustomerOption {
  id:        number;
  name:      string;
  id_number: string | null;
  phone:     string | null;
  email:     string | null;
  city:      string | null;
  street:    string | null;
}

export interface RivhitCustomerRequest {
  reason:            'not_found' | 'no_business_id' | 'branches' | 'stale_link';
  client:            { name: string; business_id: string | null; phone: string | null; email: string | null };
  customers:         RivhitCustomerOption[];
  branch_ids:        number[];
  general_available: boolean;
}

export type RivhitCustomerChoice = { mode: 'existing'; id: number } | { mode: 'general' } | { mode: 'create' };

export function parseRivhitCustomerRequest(data: unknown): RivhitCustomerRequest | null {
  if (!data || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;
  if (d.needs_customer !== true || !d.client || typeof d.client !== 'object') return null;
  return {
    reason:            (['not_found', 'no_business_id', 'branches', 'stale_link'] as const).find(r => r === d.reason) ?? 'not_found',
    client:            d.client as RivhitCustomerRequest['client'],
    customers:         Array.isArray(d.customers) ? (d.customers as RivhitCustomerOption[]) : [],
    branch_ids:        Array.isArray(d.branch_ids) ? (d.branch_ids as number[]) : [],
    general_available: d.general_available === true,
  };
}

const REASON_TEXT: Record<RivhitCustomerRequest['reason'], string> = {
  not_found:      'לא נמצא ברווחית לקוח עם אותו ח.פ. בחרו לקוח קיים, הקימו לקוח חדש או הפיקו ללקוח הכללי.',
  no_business_id: 'בכרטיס הלקוח לא מוגדר ח.פ, ולכן לא ניתן לזהות אותו ברווחית אוטומטית.',
  stale_link:     'הלקוח שהיה מקושר בכרטיס כבר לא קיים ברווחית. בחרו לקוח אחר.',
  branches:       'ברווחית יש כמה לקוחות עם אותו ח.פ. בחרו לאיזה סניף להפיק. הבחירה לא נשמרת, ותתבקשו לבחור בכל הפקה.',
};

const MAX_ROWS = 60;
const normName = (s: string) => s.toLowerCase().replace(/["'`״׳.,\-()\s]/g, '').replace(/(בעמ|ltd)$/g, '');
const phoneTail = (s: string | null) => (s ?? '').replace(/\D/g, '').slice(-9);

function similarity(c: RivhitCustomerOption, client: RivhitCustomerRequest['client']) {
  let score = 0;
  const a = normName(c.name), b = normName(client.name);
  if (a && b && a === b) score += 3;
  else if (a.length > 2 && b.length > 2 && (a.includes(b) || b.includes(a))) score += 2;
  const p = phoneTail(client.phone);
  if (p.length >= 7 && phoneTail(c.phone) === p) score += 2;
  if (client.email && c.email && client.email.trim().toLowerCase() === c.email.toLowerCase()) score += 2;
  return score;
}

interface Props {
  request:  RivhitCustomerRequest;
  busy:     boolean;
  error:    string | null;
  onChoose: (choice: RivhitCustomerChoice) => void;
  onCancel: () => void;
}

export function RivhitCustomerPickerModal({ request, busy, error, onChoose, onCancel }: Props) {
  const [query,    setQuery]    = useState('');
  const [selected, setSelected] = useState<number | null>(null);
  const branches = request.reason === 'branches' || request.branch_ids.length > 1;
  const { client } = request;

  const ranked = useMemo(() => {
    const pool = branches ? request.customers.filter(c => request.branch_ids.includes(c.id)) : request.customers;
    return pool
      .map(c => ({ c, score: branches ? 0 : similarity(c, client) }))
      .sort((x, y) => y.score - x.score || x.c.name.localeCompare(y.c.name, 'he'));
  }, [request, branches, client]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ranked;
    const qDigits = q.replace(/\D/g, '');
    return ranked.filter(({ c }) =>
      c.name.toLowerCase().includes(q) ||
      String(c.id) === q ||
      (qDigits.length >= 3 && ((c.id_number ?? '').includes(qDigits) || (c.phone ?? '').replace(/\D/g, '').includes(qDigits))));
  }, [ranked, query]);

  const shown = visible.slice(0, MAX_ROWS);

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4" dir="rtl" onClick={() => !busy && onCancel()}>
      <div className="w-full max-w-2xl max-h-[92vh] flex flex-col bg-white rounded-2xl shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold text-gray-900">{branches ? 'בחירת סניף ברווחית' : 'לא זיהיתי את הלקוח'}</h2>
          <button onClick={onCancel} disabled={busy} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4 border-b border-gray-100">
          <p className="text-sm text-gray-600 leading-relaxed">{REASON_TEXT[request.reason]}</p>
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2 text-sm bg-gray-50 border border-gray-100 rounded-xl px-4 py-3">
            <div><dt className="text-xs text-gray-500">לקוח אצלנו</dt><dd className="font-medium text-gray-900 truncate">{client.name}</dd></div>
            <div><dt className="text-xs text-gray-500">ח.פ</dt><dd className="font-medium text-gray-900" dir="ltr">{client.business_id || '—'}</dd></div>
            <div><dt className="text-xs text-gray-500">טלפון</dt><dd className="font-medium text-gray-900" dir="ltr">{client.phone || '—'}</dd></div>
            <div><dt className="text-xs text-gray-500">מייל</dt><dd className="font-medium text-gray-900 truncate" dir="ltr">{client.email || '—'}</dd></div>
          </dl>
          <div className="relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="חיפוש לפי שם, ח.פ, טלפון או מספר לקוח ברווחית"
              className="w-full pr-9 pl-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-2 min-h-[180px]">
          {shown.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-10 text-sm text-gray-500">
              <Users className="w-6 h-6 text-gray-300" />
              {request.customers.length === 0 ? 'אין עדיין לקוחות בחשבון הרווחית' : 'לא נמצאו לקוחות שתואמים לחיפוש'}
            </div>
          ) : (
            <ul className="space-y-1">
              {shown.map(({ c, score }) => {
                const active = selected === c.id;
                const address = [c.street, c.city].filter(Boolean).join(', ');
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => setSelected(c.id)}
                      onDoubleClick={() => !busy && onChoose({ mode: 'existing', id: c.id })}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border text-right transition-all ${active ? 'border-sky-400 bg-sky-50 ring-1 ring-sky-300' : 'border-transparent hover:bg-gray-50 hover:border-gray-200'}`}
                    >
                      <span className={`flex items-center justify-center w-5 h-5 rounded-full border shrink-0 transition-colors ${active ? 'bg-sky-600 border-sky-600 text-white' : 'border-gray-300'}`}>
                        {active && <Check className="w-3 h-3" />}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900 truncate">{c.name}</span>
                          {score > 0 && <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-amber-100 text-amber-800 shrink-0">דומה</span>}
                        </span>
                        <span className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5 text-xs text-gray-500">
                          <span>מס' לקוח {c.id}</span>
                          {c.id_number && <span>ח.פ {c.id_number}</span>}
                          {c.phone && <span dir="ltr">{c.phone}</span>}
                          {address && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{address}</span>}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {visible.length > MAX_ROWS && (
            <p className="px-3 py-2 text-xs text-gray-500">מוצגים {MAX_ROWS} מתוך {visible.length} לקוחות. צמצמו בעזרת החיפוש.</p>
          )}
        </div>

        {error && (
          <div className="mx-6 mb-3 flex items-start gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />{error}
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2 px-6 py-4 bg-gray-50 border-t border-gray-100">
          <div className="flex flex-wrap gap-2">
            {!branches && (
              <button onClick={() => onChoose({ mode: 'create' })} disabled={busy}
                      className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-sky-700 border border-sky-200 bg-white hover:bg-sky-50 rounded-lg transition-colors disabled:opacity-50">
                <UserPlus className="w-4 h-4" />הקם לקוח חדש ברווחית
              </button>
            )}
            {request.general_available && (
              <button onClick={() => onChoose({ mode: 'general' })} disabled={busy}
                      className="px-3 py-2 text-sm font-medium text-gray-700 border border-gray-200 bg-white hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50">
                לקוח כללי
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={onCancel} disabled={busy} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50">
              ביטול
            </button>
            <button onClick={() => selected !== null && onChoose({ mode: 'existing', id: selected })} disabled={busy || selected === null}
                    className="flex items-center gap-2 px-4 py-2 text-white text-sm font-medium rounded-lg bg-sky-600 hover:bg-sky-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" />מפיק…</> : 'בחר לקוח'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
